import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Loader2, Trash2, Pencil, Check, X, Building2, Briefcase, ArrowLeft, Mail, Save } from 'lucide-react';
import {
  listDepartments, createDepartment, updateDepartment, deleteDepartment,
  listJobTitles, createJobTitle, updateJobTitle, deleteJobTitle,
  getContactSettings, updateContactSettings,
} from '../../services/api';
import { toast } from '../../hooks/use-toast';

const TABS = [
  { key: 'departments', label: 'Departments', icon: Building2 },
  { key: 'job-titles', label: 'Job Titles', icon: Briefcase },
  { key: 'contact', label: 'Contact Info', icon: Mail },
];

const MastersPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'departments';
  const returnTo = params.get('returnTo');

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    next.set('tab', key);
    setParams(next, { replace: true });
  };

  return (
    <div data-testid="admin-masters-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Configuration</span>
          <h1 className="mt-2 text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            Masters
          </h1>
          <p className="mt-2 text-sm text-[#0b1a2e]/60">
            Manage the shared vocabulary the CRM uses — departments and job titles.
          </p>
        </div>
        {returnTo && (
          <button
            onClick={() => navigate(returnTo)}
            data-testid="masters-return-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0b1a2e] text-white text-sm font-medium hover:bg-[#0f5c3d]"
          >
            <ArrowLeft className="w-4 h-4" /> Back to previous
          </button>
        )}
      </div>

      <div className="flex gap-2 border-b border-black/10 mb-6">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`masters-tab-${t.key}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t.key
                ? 'border-[#0f5c3d] text-[#0f5c3d]'
                : 'border-transparent text-[#0b1a2e]/50 hover:text-[#0b1a2e]'
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'departments' && <MasterList kind="department" />}
      {tab === 'job-titles' && <MasterList kind="job-title" />}
      {tab === 'contact' && <ContactInfoEditor />}
    </div>
  );
};

const ContactInfoEditor = () => {
  const [form, setForm] = useState({ email: '', phone: '', address_lines: [''] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    getContactSettings().then((d) => setForm({
      email: d.email || '', phone: d.phone || '',
      address_lines: d.address_lines?.length ? d.address_lines : [''],
    })).finally(() => setLoading(false));
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      const clean = { ...form, address_lines: form.address_lines.filter((l) => l.trim()) };
      await updateContactSettings(clean);
      toast({ title: 'Contact info saved', description: 'Footer and PDFs will update on next reload.' });
    } catch (err) { toast({ title: 'Save failed', description: err?.response?.data?.detail || String(err), variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-[#0f5c3d]" />;
  return (
    <div className="bg-white border border-black/5 rounded-2xl p-6 max-w-2xl space-y-4" data-testid="contact-info-editor">
      <p className="text-xs text-[#0b1a2e]/60">Shown in the site footer, on generated PDFs, and as the "from" address for outbound emails. Leave phone blank to hide it.</p>
      <label className="block">
        <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Company Email</span>
        <input type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="info@emeraldcorporates.com" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
      </label>
      <label className="block">
        <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Company Telephone</span>
        <input type="tel" data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="+44 20 1234 5678" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
        <span className="mt-1 block text-[10px] text-[#0b1a2e]/50">Include country code. Renders as a clickable tel: link.</span>
      </label>
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Registered Address (one line per row)</div>
        {form.address_lines.map((line, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input value={line} onChange={(e) => { const a = [...form.address_lines]; a[i] = e.target.value; setForm({ ...form, address_lines: a }); }}
              data-testid={`contact-addr-${i}`} className="flex-1 border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
            <button onClick={() => setForm({ ...form, address_lines: form.address_lines.filter((_, j) => j !== i) })} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={() => setForm({ ...form, address_lines: [...form.address_lines, ''] })} className="inline-flex items-center gap-1 text-xs text-[#0f5c3d]"><Plus className="w-3.5 h-3.5" /> Add line</button>
      </div>
      <div className="pt-3 border-t border-black/5">
        <button onClick={save} disabled={busy} data-testid="contact-save" className="inline-flex items-center gap-1.5 bg-[#0f5c3d] text-white rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save contact info
        </button>
      </div>
    </div>
  );
};

const KIND_CONFIG = {
  'department': {
    testId: 'dept',
    singular: 'department',
    plural: 'Departments',
    placeholder: 'e.g. Bullion Desk, Legal, Ops…',
    list: listDepartments,
    create: createDepartment,
    update: updateDepartment,
    remove: deleteDepartment,
  },
  'job-title': {
    testId: 'jt',
    singular: 'job title',
    plural: 'Job Titles',
    placeholder: 'e.g. Sales Manager, Analyst, Ops Lead…',
    list: listJobTitles,
    create: createJobTitle,
    update: updateJobTitle,
    remove: deleteJobTitle,
  },
};

const MasterList = ({ kind }) => {
  const cfg = KIND_CONFIG[kind];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: '', description: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await cfg.list();
      setItems(r.data || []);
    } catch { toast({ title: `Failed to load ${cfg.plural.toLowerCase()}`, variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [cfg]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await cfg.create({ name: name.trim(), description: description.trim(), active: true });
      setName('');
      setDescription('');
      toast({ title: `${cfg.singular[0].toUpperCase() + cfg.singular.slice(1)} added` });
      load();
    } catch (err) {
      toast({ title: 'Failed', variant: 'destructive', description: err?.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditDraft({ name: item.name, description: item.description || '' });
  };
  const cancelEdit = () => { setEditingId(null); setEditDraft({ name: '', description: '' }); };
  const saveEdit = async (id) => {
    try {
      await cfg.update(id, { name: editDraft.name.trim(), description: editDraft.description.trim() });
      toast({ title: 'Updated' });
      cancelEdit();
      load();
    } catch (err) {
      toast({ title: 'Update failed', variant: 'destructive', description: err?.response?.data?.detail || '' });
    }
  };
  const toggleActive = async (item) => {
    try {
      await cfg.update(item.id, { active: !item.active });
      load();
    } catch (err) { toast({ title: 'Update failed', variant: 'destructive' }); }
  };
  const remove = async (item) => {
    if (!window.confirm(`Delete ${cfg.singular} "${item.name}"?`)) return;
    try { await cfg.remove(item.id); load(); }
    catch (err) { toast({ title: 'Delete failed', variant: 'destructive', description: err?.response?.data?.detail || '' }); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <form onSubmit={add} className="lg:col-span-1 bg-white border border-black/5 rounded-2xl p-5 h-fit space-y-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#0f5c3d] font-semibold">Add new</div>
          <div className="text-lg font-semibold text-[#0b1a2e] mt-1">New {cfg.singular}</div>
        </div>
        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Name</span>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={cfg.placeholder}
            data-testid={`${cfg.testId}-new-name`}
            className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1.5">Description (optional)</span>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid={`${cfg.testId}-new-description`}
            className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm"
          />
        </label>
        <button
          type="submit"
          disabled={saving}
          data-testid={`${cfg.testId}-add-btn`}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0f5c3d] text-white text-sm font-medium disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add {cfg.singular}
        </button>
      </form>

      <div className="lg:col-span-2 bg-white border border-black/5 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#0b1a2e]/50">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-[#0b1a2e]/50 text-sm">No {cfg.plural.toLowerCase()} yet. Add your first on the left.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Description</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const isEditing = editingId === item.id;
                return (
                  <tr key={item.id} data-testid={`${cfg.testId}-row`} className="border-t border-black/5 hover:bg-[#f7fbf8]/40">
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <input
                          value={editDraft.name}
                          onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                          className="w-full border border-[#0f5c3d]/15 rounded px-2 py-1 text-sm"
                        />
                      ) : (
                        <span className="font-medium text-[#0b1a2e]">{item.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#0b1a2e]/70">
                      {isEditing ? (
                        <input
                          value={editDraft.description}
                          onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })}
                          className="w-full border border-[#0f5c3d]/15 rounded px-2 py-1 text-sm"
                        />
                      ) : (item.description || <span className="text-[#0b1a2e]/30">—</span>)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleActive(item)}
                        className={`text-xs font-medium ${item.active !== false ? 'text-emerald-700' : 'text-red-600'}`}
                      >
                        {item.active !== false ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button onClick={() => saveEdit(item.id)} className="p-1.5 rounded-lg text-emerald-700 hover:bg-emerald-50"><Check className="w-4 h-4" /></button>
                          <button onClick={cancelEdit} className="p-1.5 rounded-lg text-[#0b1a2e]/60 hover:bg-black/5"><X className="w-4 h-4" /></button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(item)} data-testid={`${cfg.testId}-edit-btn`} className="p-1.5 rounded-lg text-[#0b1a2e]/70 hover:bg-black/5"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => remove(item)} data-testid={`${cfg.testId}-delete-btn`} className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MastersPage;
